import React, { Component } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { Layout, Menu, Grid, Button, Drawer, Space, Spin, Select } from 'antd';
import {
  FormOutlined,
  UnorderedListOutlined,
  MenuOutlined,
  LogoutOutlined,
  HistoryOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  InboxOutlined,
  ExperimentOutlined,
  CarOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  TeamOutlined,
} from '@ant-design/icons';

import { useAuth, AuthContext  } from '../context/AuthContext';

import { authService } from '../services/authService';
import OrderEntry from './OrderEntry/OrderEntry';
import OrderHistory from './OrderHistory/OrderHistory';
import ReviewResults from './Results/ReviewResults';
import AccessionDetail from './Results/AccessionDetail';
import Order from './supplies/Order';
import SupplyOrderHistory from './supplies/OrderHistory';
import PickupRequest from './supplies/PickupRequest';
import OrderSets from './Tests/OrderSets';
import ActionItems from './ActionItems/ActionItems';
import UserManagement from './Users/UserManagement';
import { API_BASE_URL } from '../config/api';

const { Content, Footer, Sider, Header } = Layout;
const { useBreakpoint } = Grid;

const ACTIVE_CLIENT_KEY = 'active_client_id';

const menuItems = [
  {
    key: '/order-entry',
    icon: <FormOutlined />,
    label: <Link to="/order-entry">Order Entry</Link>,
  },
   {
    key: '/order-history',
    icon: <HistoryOutlined />,
    label: <Link to="/order-history">Order History</Link>,
  },
  {
    key: '/results',
    icon: <UnorderedListOutlined />,
    label: <Link to="/results">Results</Link>,
  },
  {
    key: 'supplies',
    icon: <InboxOutlined />,
    label: 'Supplies',
    children: [
      {
        key: '/supply-order',
        icon: <ShoppingCartOutlined />,
        label: <Link to="/supply-order">Supply Order</Link>,
      },
      {
        key: '/supply-order-history',
        icon: <ShoppingOutlined />,
        label: <Link to="/supply-order-history">Order History</Link>,
      },
    ],
  },
  {
    key: '/pickup-request',
    icon: <CarOutlined />,
    label: <Link to="/pickup-request">Pickup Request</Link>,
  },
  {
    key: '/action-items',
    icon: <SafetyCertificateOutlined />,
    label: <Link to="/action-items">Action Items</Link>,
  },
  {
    key: '/test-directory',
    icon: <ExperimentOutlined />,
    label: <Link to="/test-directory">Test Directory</Link>,
  },
];

// connectPermissions column behind each menu item. The `supplies` flag covers
// the whole Supplies submenu (all or nothing).
const PAGE_PERMISSIONS = {
  '/order-entry': 'orderEntry',
  '/order-history': 'orderHistory',
  '/results': 'results',
  'supplies': 'supplies',
  '/pickup-request': 'pickupRequest',
  '/action-items': 'actionItems',
  '/test-directory': 'testDirectory',
};

// No permissions object (older backend) or a flag that isn't explicitly 0
// means allowed — restrictions are opt-in.
const can = (permissions, menuKey) =>
  !permissions || permissions[PAGE_PERMISSIONS[menuKey]] !== 0;

const buildMenuItems = (permissions) => {
  const items = menuItems.filter(item => can(permissions, item.key));
  if (permissions?.isClientAdmin === 1) {
    items.push({
      key: '/user-management',
      icon: <TeamOutlined />,
      label: <Link to="/user-management">User Management</Link>,
    });
  }
  return items;
};

// Where "/" should land: Results when allowed, otherwise the first permitted
// page in menu order.
const homePath = (permissions) => {
  if (can(permissions, '/results')) return '/results';
  const fallbacks = [
    ['/order-entry', '/order-entry'],
    ['/order-history', '/order-history'],
    ['supplies', '/supply-order'],
    ['/pickup-request', '/pickup-request'],
    ['/action-items', '/action-items'],
    ['/test-directory', '/test-directory'],
  ];
  const first = fallbacks.find(([menuKey]) => can(permissions, menuKey));
  if (first) return first[1];
  return permissions?.isClientAdmin === 1 ? '/user-management' : null;
};

// Shows the active account. When the user has access to more than one client,
// renders a dropdown; switching returns to the home page so the user never
// stays on a detail view that belongs to the previous client.
const ClientSwitcher = ({ client, clients, onClientChange, collapsed, style }) => {
  const navigate = useNavigate();

  const labelStyle = {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 14,
    ...style,
  };

  if (collapsed || !clients || clients.length <= 1) {
    return (
      <div style={labelStyle}>
        {collapsed ? client.ClientCode : `Account: ${client.ClientCode}`}
      </div>
    );
  }

  return (
    <div style={{ ...labelStyle, marginRight: 20 }}>
      <div style={{ marginBottom: 6 }}>Account:</div>
      <Select
        value={client.id}
        onChange={(clientId) => {
          navigate('/');
          onClientChange(clientId);
        }}
        options={clients.map((c) => ({ value: c.id, label: c.ClientCode }))}
        style={{ width: '100%' }}
      />
    </div>
  );
};

// Top bar naming the active location: the side menu only shows client CODES,
// and users don't always know which code is which office — this line is the
// answer, always visible above the content.
const ActiveClientBar = ({ client, compact }) => {
  const address = [client.Address, client.City, client.State, client.Zip]
    .filter(Boolean).join(', ');

  return (
    <div
      style={{
        background: '#fff',
        padding: compact ? '6px 12px' : '0 16px',
        height: compact ? undefined : 44,
        lineHeight: compact ? undefined : '44px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: compact ? 12 : 13,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      <EnvironmentOutlined style={{ color: '#1677ff', flexShrink: 0 }} />
      <span style={{ fontWeight: 600 }}>{client.ClientName}</span>
      <span style={{ color: '#8c8c8c' }}>#{client.ClientCode}</span>
      {address && (
        <span style={{ color: '#595959', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {address}
        </span>
      )}
    </div>
  );
};

const Shell = ({ collapsed, setCollapsed, client, clients, onClientChange, printers, permissions }) => {
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { logout } = useAuth();

  const path = location.pathname;

  const selectedKey =
  path === '/' || 
  path.startsWith('/results') ? '/results' :
  path.startsWith('/order-entry') ? '/order-entry' :  
  path.startsWith('/order-history') ? '/order-history' :
  path;

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const menu = (
    <Menu
      mode="inline"
      theme="dark"
      selectedKeys={[selectedKey]}
      items={buildMenuItems(permissions)}
      style={{
        background: '#001529',
        borderRight: 'none',
      }}
      onClick={() => {
        if (isMobile) setDrawerOpen(false);
      }}
    />
  );

  // ONE tree for both mobile and desktop. The chrome (top header, sider,
  // drawer) toggles with the breakpoint, but the ancestors of <Content> keep
  // the same types and positions either way — so rotating a phone across the
  // md breakpoint restyles the chrome WITHOUT remounting the current screen.
  // (The old two-tree version remounted everything on rotation, wiping any
  // half-filled form.)
  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
          }}
        >
          <div
            style={{
              color: '#fff',
              fontWeight: 'bold',
              fontSize: 18,
              padding: '0 8px',
            }}
          >
            Lincoln Connect
          </div>

          <Space>
            <Button
              type="text"
              onClick={logout}
              icon={<LogoutOutlined style={{ color: '#fff', fontSize: 18 }} />}
            />
            <Button
              type="text"
              onClick={() => setDrawerOpen(true)}
              icon={<MenuOutlined style={{ color: '#fff', fontSize: 20 }} />}
            />
          </Space>
        </Header>
      ) : null}

      <Layout>
        {isMobile ? null : (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
          >
            <div
              style={{
                color: '#fff',
                fontWeight: 'bold',
                fontSize: 18,
                marginLeft: 20,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              {collapsed ? ' ' : 'Lincoln Connect'}
            </div>

            {menu}
            <ClientSwitcher
              client={client}
              clients={clients}
              onClientChange={onClientChange}
              collapsed={collapsed}
              style={{ marginLeft: collapsed ? 30 : 20, marginTop: 20, marginBottom: 10 }}
            />
            <div style={{
              position: 'absolute',
              width: '100%',
              marginLeft: 25,
              marginTop: 20,
              marginBottom: 10,
            }}>
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={logout}
                style={{ color: 'white' }}
              >
                {collapsed ? '' : 'Logout'}
              </Button>
            </div>
          </Sider>
        )}

        <Layout>
          <ActiveClientBar client={client} compact={isMobile} />
          <Content style={{ padding: isMobile ? 12 : 16 }}>
            <Routes>
              {/* Routes mirror the menu permissions; the "/" default follows
                  the user to their first permitted page. */}
              <Route path="/" element={
                homePath(permissions) ?
                  (can(permissions, '/results') ?
                    <ReviewResults client={client}/> :
                    <Navigate to={homePath(permissions)} replace />) :
                  <div style={{ padding: 24 }}>Your account has no pages enabled. Please contact your administrator.</div>
              } />
              {can(permissions, '/order-entry') && <Route path="/order-entry" element={<OrderEntry client={client} printers={printers}/>} />}
              {can(permissions, '/order-entry') && <Route path="/order-entry/:requisitionID" element={<OrderEntry client={client} printers={printers}/>} />}
              {can(permissions, '/order-history') && <Route path="/order-history" element={<OrderHistory client={client} printers={printers}/>} />}
              {can(permissions, '/results') && <Route path="/results" element={<ReviewResults client={client}/>} />}
              {can(permissions, '/results') && <Route path="/results/:accessionID" element={<AccessionDetail client={client} />} />}
              {can(permissions, 'supplies') && <Route path="/supply-order" element={<Order client={client}/>} />}
              {can(permissions, '/pickup-request') && <Route path="/pickup-request" element={<PickupRequest client={client}/>} />}
              {can(permissions, '/action-items') && <Route path="/action-items" element={<ActionItems client={client}/>} />}
              {can(permissions, 'supplies') && <Route path="/supply-order-history" element={<SupplyOrderHistory client={client}/>} />}
              {can(permissions, '/test-directory') && <Route path="/test-directory" element={<OrderSets/>} />}
              {permissions?.isClientAdmin === 1 && <Route path="/user-management" element={<UserManagement client={client}/>} />}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Content>

          <Footer style={{ textAlign: 'center' }}>
            ©{new Date().getFullYear()} Lincoln Reference Laboratory
          </Footer>
        </Layout>
      </Layout>

      {isMobile ? (
        <Drawer
          placement="top"
          closable={false}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          styles={{
            header: { display: 'none' },
            body: {
              padding: 0,
              background: '#001529',
            },
          }}
        >
          {menu}
          <ClientSwitcher
            client={client}
            clients={clients}
            onClientChange={onClientChange}
            style={{ marginLeft: 20, marginTop: 20, marginBottom: 10 }}
          />
        </Drawer>
      ) : null}
    </Layout>
  );
};

class App extends Component {
  static contextType = AuthContext;
  constructor(props) {
    super(props);
    this.state = {
      collapsed: false,
      client: {},
      clients: [],
      userID: '',
      permissions: null,
      printers: [],

      loading: true,
    };
  }

  componentDidMount = () => {
    this.initDymo();
    this.profile()
  }

  initDymo = async () => {
  const dymo = window.dymo;

  if (!dymo?.label?.framework) {
    console.error("DYMO framework not found. Make sure dymo.connect.framework.js is loaded.");
    return;
  }

  try {
    await dymo.label.framework.init();
  } catch (e) {
    console.warn("DYMO init not ready yet:", e?.message || e);
  }

  this.waitForDymoService();
};

waitForDymoService = () => {
  const fw = window.dymo?.label?.framework;
  if (!fw) return;

  let tries = 0;
  const maxTries = 60; // 6 seconds
  let hasStopped = false; // Flag to prevent further checks

  const tick = async () => {
    if (hasStopped) return; // Stop if already stopped
    
    tries += 1;

    try {
      const ok = fw.checkEnvironment();
      if (ok) {

        // Get printers
        const printers = await fw.getLabelWriterPrintersAsync();
        console.log("LabelWriter printers:", printers);

        // Check if any printers were found
        if (!printers || printers.length === 0) {
          console.error("❌ No DYMO printers found. Please connect a printer.");
          hasStopped = true;
          this.setState({
            printers: [],
            printerError: "No DYMO printers found"
          });
          return;
        }

        // Printers found - store them
        hasStopped = true;
        this.dymoPrinters = printers;
        this.setState({
          printers: printers,
          printerError: null
        });
        return;
      }
    } catch (e) {
      const msg = e?.message || String(e);
      
      // Check for plugin not installed error
      if (msg.includes("Plugin is not installed") || msg.includes("ERR_CONNECTION_REFUSED")) {
        console.error("❌ DYMO Label Framework Plugin is not installed or DYMO Connect is not running.");
        hasStopped = true;
        this.setState({
          printers: [],
          printerError: "DYMO Plugin not installed or service not running"
        });
        return;
      }
      
      // Ignore discovery noise
      if (!msg.toLowerCase().includes("discovery")) {
        console.warn("DYMO check failed:", msg);
      }
    }

    if (tries >= maxTries) {
      console.error("❌ DYMO service not discovered. Is DYMO Connect running?");
      hasStopped = true;
      this.setState({
        printers: [],
        printerError: "DYMO service timeout"
      });
      return;
    }

    // Only continue if not stopped
    if (!hasStopped) {
      setTimeout(tick, 100);
    }
  };
  tick();
};

  profile = async () => {
    let decoded = authService.getUserAttributes();

    const token = await authService.ensureValidToken();
    if (!token) {
      this.context.logout();
      return;
    }

    axios({
      method: 'GET',
      url: `${API_BASE_URL}/connect/profile/${decoded.sub}`,
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      console.log(res)
      // Supports both response shapes: `clients` (array, multi-client users)
      // and the legacy single `client` object.
      const clients = Array.isArray(res.data.clients) && res.data.clients.length > 0
        ? res.data.clients
        : res.data.client ? [res.data.client] : [];

      if (clients.length > 0){
        // Restore the last-used account; the stored id must belong to this
        // user's list, otherwise fall back to the first client.
        const storedID = localStorage.getItem(ACTIVE_CLIENT_KEY);
        const active = clients.find(c => String(c.id) === storedID) || clients[0];
        this.setState({
          client: active,
          clients: clients,
          userID: res.data.keycloakUser.id,
          permissions: res.data.permissions || null,
          loading: false,
        })
      } else {
        this.context.logout()
      }
    }).catch(err =>{
      console.log(err);
      this.context.logout()
    })
  }

  setActiveClient = (clientId) => {
    const next = this.state.clients.find(c => c.id === clientId);
    if (!next || next.id === this.state.client.id) return;
    localStorage.setItem(ACTIVE_CLIENT_KEY, String(next.id));
    this.setState({ client: next });
  }

  setCollapsed = (collapsed) => {
    this.setState({ collapsed });
  };

  render() {
    return (
      <>
        <Router>
          {this.state.loading ? 
            <Spin fullscreen /> :
            <Shell
              key={this.state.client.id}
              collapsed={this.state.collapsed}
              setCollapsed={this.setCollapsed}
              client = {this.state.client}
              clients = {this.state.clients}
              onClientChange = {this.setActiveClient}
              printers = {this.state.printers}
              permissions = {this.state.permissions}
            />
          }
          
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
        />
      </>      
    );
  }
}

export default App;
